<template>
    <div class="invitation-card" v-if="invitation">
        <div class="ic-row">
            <div class="ic-avatar">
                <v-img v-if="coachAvatar" eager cover :src="coachAvatar" class="ic-avatar-img" />
                <v-icon v-else size="22" color="txtMuted">mdi-account</v-icon>
            </div>

            <div class="ic-body">
                <div class="ic-name text-truncate">{{ coach.name }}</div>
                <div class="ic-meta">
                    <span class="ic-time">{{ timestamp }}</span>
                    <span class="ic-status" :class="`ic-status--${invitation.status}`">
                        <v-icon size="12" :color="statusIcon.color">{{ statusIcon.icon }}</v-icon>
                        {{ status }}
                    </span>
                </div>
            </div>
        </div>

        <div v-if="invitation.status === 'pending'" class="ic-actions">
            <v-btn depressed height="36" class="normal-btn ic-btn ic-btn-reject" @click="reject">
                {{ $t('common.reject') }}
            </v-btn>
            <v-btn depressed height="36" class="normal-btn ic-btn ic-btn-accept black--text" @click="accept">
                {{ $t('common.accept') }}
            </v-btn>
        </div>

        <kimsa-confirm
            ref="acceptInvitationDialog"
            :title="$t('student.dialog_accept_invitation_title')"
            :content="$t('student.dialog_accept_invitation_body')"
            :loading="loadingAccept"
            :close-on-confirm="false"
            :confirm-label="$t('common.accept')"
            @confirm="acceptConfirm"
        />

        <kimsa-confirm
            ref="rejectInvitationDialog"
            alert-mode
            :title="$t('student.dialog_reject_invitation_title')"
            :content="$t('student.dialog_reject_invitation_body')"
            :loading="loadingReject"
            :close-on-confirm="false"
            :confirm-label="$t('common.reject')"
            @confirm="rejectConfirm"
        />
    </div>
</template>

<script>

import firebase from "@/utils/Firebase";
import {getCoachClientsLimit} from "@/utils/Helpers";
import { toDate } from "@/utils/Common";
import KimsaConfirm from "@/components/common/KimsaConfirm";

export default {
    components: {KimsaConfirm},
    props: {
        invitation: {
            type: Object,
            default: null,
        }
    },
    data() {
        return {
            operationType: 'accept',
            loadingAccept: false,
            loadingReject: false,
        }
    },
    computed: {
        coach() {
            const vm = this
            return vm.invitation?.coach || null
        },
        coachAvatar(vm = this) {
            return vm.coach?.avatar || null
        },
        timestamp() {
            const vm = this
            const createdAt = toDate(vm.invitation?.createdAt)
            if (!createdAt) return ''
            return vm.$moment(createdAt).fromNow()
        },
        status() {
            const vm = this
            if (!vm.invitation) return vm.$t('common.invitation_status.pending')
            return vm.$t(`common.invitation_status.${vm.invitation.status}`)
        },
        statusIcon() {
            const vm = this
            if (vm.invitation.status === 'pending') return {icon: 'mdi-timer-sand', color: 'txtMuted'}
            if (vm.invitation.status === 'accepted') return {icon: 'mdi-check-circle-outline', color: 'success'}
            return {icon: 'mdi-block-helper', color: 'error'}
        }
    },
    methods: {
        acceptInvitation: firebase.functions().httpsCallable('httpInvitations-acceptStudentInvitation'),
        rejectInvitation: firebase.functions().httpsCallable('httpInvitations-rejectStudentInvitation'),
        async acceptConfirm() {
            const vm = this
            vm.loadingAccept = true

            if (!await vm.checkCanAutoAcceptInvitation()) {
                vm.toast(vm.$t('coach.cannot_accept_invitation_limit_reached'), 'error')
                if (vm.$refs.acceptInvitationDialog) vm.$refs.acceptInvitationDialog.hide()
                return vm.loadingAccept = false
            }

            const payload = {invitationID: vm.invitation.id}
            

            vm.acceptInvitation(payload).then(result => {
                // console.log('accept result', result)
                vm.toast(vm.$t('student.invitation_accepted'))
                if (vm.$refs.acceptInvitationDialog) vm.$refs.acceptInvitationDialog.hide()
                vm.$emit('accepted')
            }).catch(err => {
                vm.toast(`${vm.$t('common.an_error_has_occurred')}: ${err.message}`, 'error')
            }).finally(() => {
                vm.loadingAccept = false
            })
        },
        async rejectConfirm() {
            const vm = this
            const payload = {invitationID: vm.invitation.id}
            vm.loadingReject = true

            vm.rejectInvitation(payload).then(result => {
                // console.log('reject result', result)
                vm.toast(vm.$t('student.invitation_rejected'))
                if (vm.$refs.rejectInvitationDialog) vm.$refs.rejectInvitationDialog.hide()
                vm.$emit('reject')
            }).catch(err => {
                vm.toast(`${vm.$t('common.an_error_has_occurred')}: ${err.message}`, 'error')
            }).finally(() => {
                vm.loadingReject = false
            })
        },
        accept() {
            const vm = this
            vm.$refs.acceptInvitationDialog.show()
        },
        reject() {
            const vm = this
            vm.$refs.rejectInvitationDialog.show()
        },
        async checkCanAutoAcceptInvitation() {
            const vm = this;
            const { canInvite } = await getCoachClientsLimit(vm.coach?.id)
            return canInvite;
        },
    },
}
</script>

<style scoped>

.invitation-card {
    padding: 14px;
    border-radius: 12px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface-base);
}

.ic-row {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.ic-avatar {
    flex-shrink: 0;
    width: 46px;
    height: 46px;
    border-radius: 50%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
}

.ic-avatar-img {
    width: 100%;
    height: 100%;
}

.ic-body {
    flex: 1;
    min-width: 0;
}

.ic-name {
    font-size: var(--fs-body);
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.ic-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 4px;
}

.ic-time {
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.ic-status {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    background: var(--v-surface2-base);
    color: var(--v-txtSecondary-base);
}

.ic-status--accepted {
    color: var(--v-success-base);
}

.ic-status--rejected {
    color: var(--v-error-base);
}

.ic-actions {
    display: flex;
    gap: 8px;
    margin-top: 14px;
}

.ic-btn {
    flex: 1 1 0;
}

.ic-btn-reject {
    background: var(--v-surface2-base) !important;
    border: 1px solid var(--v-outline-base);
    color: var(--v-txtSecondary-base) !important;
}

.ic-btn-accept {
    background: var(--brand-gradient) !important;
}

</style>